import React, { useEffect, useRef, useState } from "react";

// Small, dependency-free chart pieces for the task dashboard. Single-series
// marks use --tk-series-1; text always uses text tokens, never the mark colour.

function niceMax(value) {
  if (value <= 4) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const steps = [1, 2, 2.5, 5, 10];
  const step = steps.find((s) => s * magnitude >= value / 4) * magnitude;
  return Math.ceil(value / step) * step;
}

function Tooltip({ tip }) {
  if (!tip) return null;
  return (
    <div
      className="tk-viz-tooltip"
      role="status"
      style={{ left: `${tip.x}%`, top: tip.y }}
    >
      <strong>{tip.value}</strong>
      <span>{tip.label}</span>
    </div>
  );
}

// Vertical columns over a time axis, e.g. tasks completed per day.
export function ColumnChart({ data, valueLabel, formatX, ariaLabel }) {
  const [tip, setTip] = useState(null);
  const [showTable, setShowTable] = useState(false);

  // Draw at the container's real pixel width so text and bars keep their
  // size on any screen (a scaled viewBox shrinks text on phones).
  const plotRef = useRef(null);
  const [width, setWidth] = useState(640);
  useEffect(() => {
    const el = plotRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const next = Math.round(entry.contentRect.width);
      if (next > 0) setWidth(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [showTable]);
  const height = 200;
  const pad = { top: 18, right: 8, bottom: 26, left: 28 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const max = niceMax(Math.max(0, ...data.map((d) => d.value)));
  const band = plotW / data.length;
  const barW = Math.min(24, band - 2);
  const peak = data.reduce(
    (best, d, i) => (d.value > (data[best]?.value ?? -1) ? i : best),
    0,
  );
  const ticks = [0, max / 2, max];
  // Roughly one date label per 70px so they never collide on narrow screens.
  const labelEvery = Math.ceil(
    data.length / Math.max(2, Math.floor(plotW / 70)),
  );

  const show = (d, i) =>
    setTip({
      index: i,
      x: ((pad.left + band * i + band / 2) / width) * 100,
      y: 0,
      value: `${d.value} ${valueLabel}`,
      label: formatX(d.key, true),
    });

  return (
    <div className="tk-viz">
      <button
        type="button"
        className="tk-link-btn tk-viz-toggle"
        onClick={() => setShowTable((v) => !v)}
      >
        {showTable ? "View chart" : "View as table"}
      </button>
      {showTable ? (
        <table className="tk-viz-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d) => (
              <tr key={d.key}>
                <td>{formatX(d.key, true)}</td>
                <td>{d.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div
          className="tk-viz-plot"
          ref={plotRef}
          onMouseLeave={() => setTip(null)}
        >
          <svg
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={ariaLabel}
          >
            {ticks.map((t) => {
              const y = pad.top + plotH - (t / max) * plotH;
              return (
                <g key={t}>
                  <line
                    x1={pad.left}
                    x2={width - pad.right}
                    y1={y}
                    y2={y}
                    className="tk-viz-grid"
                  />
                  <text
                    x={pad.left - 6}
                    y={y + 4}
                    className="tk-viz-axis"
                    textAnchor="end"
                  >
                    {Number.isInteger(t) ? t : t.toFixed(1)}
                  </text>
                </g>
              );
            })}
            {data.map((d, i) => {
              const h = (d.value / max) * plotH;
              const x = pad.left + band * i + (band - barW) / 2;
              const y = pad.top + plotH - h;
              const r = Math.min(4, h, barW / 2);
              return (
                <g key={d.key}>
                  {h > 0 && (
                    <path
                      className={`tk-viz-bar ${tip && tip.index === i ? "hover" : ""}`}
                      d={`M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + barW - r} Q${x + barW},${y} ${x + barW},${y + r} V${y + h} Z`}
                    />
                  )}
                  {i === peak && d.value > 0 && (
                    <text
                      x={x + barW / 2}
                      y={y - 5}
                      textAnchor="middle"
                      className="tk-viz-value"
                    >
                      {d.value}
                    </text>
                  )}
                  {(i % labelEvery === 0 || i === data.length - 1) && (
                    <text
                      x={pad.left + band * i + band / 2}
                      y={height - 8}
                      textAnchor="middle"
                      className="tk-viz-axis"
                    >
                      {formatX(d.key, false)}
                    </text>
                  )}
                  <rect
                    x={pad.left + band * i}
                    y={pad.top}
                    width={band}
                    height={plotH}
                    className="tk-viz-hit"
                    tabIndex={0}
                    aria-label={`${formatX(d.key, true)}: ${d.value} ${valueLabel}`}
                    onMouseEnter={() => show(d, i)}
                    onFocus={() => show(d, i)}
                    onBlur={() => setTip(null)}
                  />
                </g>
              );
            })}
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={pad.top + plotH}
              y2={pad.top + plotH}
              className="tk-viz-baseline"
            />
          </svg>
          <Tooltip tip={tip} />
        </div>
      )}
    </div>
  );
}

// Horizontal bars for a short list of categories, value at the tip.
export function BarList({ rows, total, onSelect }) {
  const [hovered, setHovered] = useState(null);
  const max = Math.max(1, ...rows.map((r) => r.value));
  if (rows.length === 0)
    return <p className="tk-muted">Nothing to show yet.</p>;

  return (
    <ul className="tk-barlist">
      {rows.map((row) => {
        const share = total ? Math.round((row.value / total) * 100) : null;
        return (
          <li
            key={row.key}
            className={hovered === row.key ? "hover" : ""}
            onMouseEnter={() => setHovered(row.key)}
            onMouseLeave={() => setHovered(null)}
            title={
              share !== null
                ? `${row.value} (${share}% of open tasks)`
                : undefined
            }
          >
            <span className="tk-barlist-label" title={row.label}>
              {row.icon}
              <span className="tk-barlist-text">{row.label}</span>
            </span>
            <span className="tk-barlist-track">
              <span
                className={`tk-barlist-fill ${row.value === 0 ? "empty" : ""}`.trim()}
                style={{ width: `${(row.value / max) * 100}%` }}
              />
            </span>
            <span className="tk-barlist-value">
              {row.value}
              {hovered === row.key && share !== null && (
                <span className="tk-muted"> · {share}%</span>
              )}
            </span>
            {onSelect && (
              <button
                type="button"
                className="tk-barlist-hit"
                aria-label={`${row.label}: ${row.value}`}
                onClick={() => onSelect(row)}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

// Ratio against 100%, track is a lighter step of the same hue.
export function Meter({ value, label }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div
      className="tk-meter"
      role="meter"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <span className="tk-meter-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}
